import { ReactNode } from "react";
import { HexColorPicker } from "react-colorful";

import { Palette } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { colors } from "@/client/constants/colors";
import { useCoverLetterStore } from "@/client/stores/cover-letter";
import { Input, Label, Popover, PopoverContent, PopoverTrigger } from "@reactive-resume/ui";

import { CLCollapsibleSection } from "../../../CollapsibleSection";

export const CLThemeSection = () => {
  const setValue = useCoverLetterStore((state) => state.setValue);
  const theme = useCoverLetterStore((state) => state.coverLetter.data.metadata.theme);

  const headerHTML: ReactNode = (
    <div className="flex items-center gap-x-4">
      <Palette size={18} />
      <h2 className="line-clamp-1 text-base font-bold text-gray-800 lg:text-lg">Theme</h2>
    </div>
  );

  const contentHTML: ReactNode = (
    <main className="grid gap-y-6">
      <div className="mb-2 grid grid-cols-6 flex-wrap justify-items-center gap-y-4 @xs/right:grid-cols-9">
        {colors.map((color) => (
          <div
            key={color}
            className={cn(
              "flex size-6 cursor-pointer items-center justify-center rounded-full ring-primary ring-offset-1 ring-offset-background transition-shadow hover:ring-1",
              theme.primary === color && "ring-1",
            )}
            onClick={() => setValue("metadata.theme.primary", color)}
          >
            <div className="size-5 rounded-full" style={{ backgroundColor: color }} />
          </div>
        ))}
      </div>

      <div className="space-y-1.5">
        <Label>Primary Color</Label>
        <div className="relative">
          <Popover>
            <PopoverTrigger asChild>
              <div
                className="absolute inset-y-0 left-3 my-2.5 size-4 cursor-pointer rounded-full ring-primary ring-offset-2 ring-offset-background transition-shadow hover:ring-1"
                style={{ backgroundColor: theme.primary }}
              />
            </PopoverTrigger>
            <PopoverContent className="rounded-lg border-none bg-transparent p-0">
              <HexColorPicker
                color={theme.primary}
                onChange={(color) => setValue("metadata.theme.primary", color)}
              />
            </PopoverContent>
          </Popover>
          <Input
            value={theme.primary}
            className="pl-10"
            onChange={(event) => setValue("metadata.theme.primary", event.target.value)}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Background Color</Label>
        <div className="relative">
          <Popover>
            <PopoverTrigger asChild>
              <div
                className="absolute inset-y-0 left-3 my-2.5 size-4 cursor-pointer rounded-full ring-primary ring-offset-2 ring-offset-background transition-shadow hover:ring-1"
                style={{ backgroundColor: theme.background }}
              />
            </PopoverTrigger>
            <PopoverContent className="rounded-lg border-none bg-transparent p-0">
              <HexColorPicker
                color={theme.background}
                onChange={(color) => setValue("metadata.theme.background", color)}
              />
            </PopoverContent>
          </Popover>
          <Input
            value={theme.background}
            className="pl-10"
            onChange={(event) => setValue("metadata.theme.background", event.target.value)}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Text Color</Label>
        <div className="relative">
          <Popover>
            <PopoverTrigger asChild>
              <div
                className="absolute inset-y-0 left-3 my-2.5 size-4 cursor-pointer rounded-full ring-primary ring-offset-2 ring-offset-background transition-shadow hover:ring-1"
                style={{ backgroundColor: theme.text }}
              />
            </PopoverTrigger>
            <PopoverContent className="rounded-lg border-none bg-transparent p-0">
              <HexColorPicker
                color={theme.text}
                onChange={(color) => setValue("metadata.theme.text", color)}
              />
            </PopoverContent>
          </Popover>
          <Input
            value={theme.text}
            className="pl-10"
            onChange={(event) => setValue("metadata.theme.text", event.target.value)}
          />
        </div>
      </div>
    </main>
  );

  return <CLCollapsibleSection id="cl-theme" header={headerHTML} content={contentHTML} />;
};
